// Enhanced API Service for Multi-Tenant Support
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/** Client primary email — shared regex for forms (real-time / submit validation). */
export const CLIENT_PRIMARY_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Returns empty string if valid, otherwise a short inline error message. */
export function getClientPrimaryEmailError(email) {
  const trimmed = String(email ?? '').trim();
  if (!trimmed) return 'Primary email is required';
  if (!CLIENT_PRIMARY_EMAIL_REGEX.test(trimmed)) return 'Enter a valid email address';
  return '';
}

/**
 * Flatten API validation payloads (e.g. DRF { field: ["msg"] }) into { field: "msg" } for inline form errors.
 */
export function mapApiErrorsToForm(data) {
  const out = {};
  if (!data || typeof data !== 'object') return out;
  const skip = new Set(['detail', 'non_field_errors', 'error', 'message']);
  Object.entries(data).forEach(([key, val]) => {
    if (skip.has(key)) return;
    if (val == null) return;
    if (Array.isArray(val)) {
      const first = val.find((x) => typeof x === 'string') || (val[0] != null ? String(val[0]) : '');
      if (first) out[key] = first;
      return;
    }
    if (typeof val === 'string') {
      out[key] = val;
      return;
    }
    if (typeof val === 'object' && !Array.isArray(val)) {
      Object.entries(val).forEach(([k2, v2]) => {
        const msg = Array.isArray(v2) ? v2[0] : v2;
        if (typeof msg === 'string') out[`${key}.${k2}`] = msg;
      });
    }
  });
  return out;
}

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add authentication token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // When sending FormData, remove Content-Type so browser sets multipart/form-data with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/api/token/refresh/`, {
            refresh: refreshToken,
          });

          const { access } = response.data;
          localStorage.setItem('access_token', access);

          originalRequest.headers.Authorization = `Bearer ${access}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed - clear all auth data and redirect to login
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        localStorage.removeItem('userId');
        localStorage.removeItem('jwtToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// Enhanced API methods with multi-tenant support
const enhancedApi = {
  // Authentication
  login: (credentials) => api.post('/api/login/', credentials),
  register: (data) => api.post('/api/register/', data),
  logout: () => api.post('/api/logout/'),
  
  // Dashboard
  getDashboardData: () => api.get('/api/dashboard-data/'),
  getDashboardV2: () => api.get('/api/dashboard/v2/'),
  getOnboardingProgress: () => api.get('/api/onboarding/progress/'),
  getProfitabilityReport: (params) => api.get('/api/reports/profitability/', { params }),
  getRevenueForecast: (months) => api.get('/api/reports/revenue-forecast/', { params: { months } }),
  getStaffUtilization: (params) => api.get('/api/reports/staff-utilization/', { params }),
  getClientActivityReport: (params) => api.get('/api/reports/client-activity/', { params }),
  exportClientActivityCSV: (params) =>
    api.get('/api/reports/client-activity/export/', { params, responseType: 'blob' }),
  getInvoiceSummaryReport: (params) => api.get('/api/reports/invoice-summary/', { params }),
  exportInvoiceSummaryCSV: (params) =>
    api.get('/api/reports/invoice-summary/export/', { params, responseType: 'blob' }),

  // Email Templates
  getEmailTemplates: (params) => api.get('/api/email-templates/', { params }),
  createEmailTemplate: (data) => api.post('/api/email-templates/', data),
  updateEmailTemplate: (id, data) => api.patch(`/api/email-templates/${id}/`, data),
  deleteEmailTemplate: (id) => api.delete(`/api/email-templates/${id}/`),
  previewEmailTemplate: (id) => api.post(`/api/email-templates/${id}/preview/`),
  sendTestEmailTemplate: (id) => api.post(`/api/email-templates/${id}/send-test/`),
  
  // File Management (Enhanced with multi-tenant)
  getFiles: () => api.get('/api/multi-tenant/files/'),
  getFileAuditLog: (fileId) => api.get(`/api/multi-tenant/files/${fileId}/audit-log/`),
  uploadFile: (formData) => api.post('/api/multi-tenant/files/upload/', formData),
  deleteFile: (fileId) => api.delete(`/api/multi-tenant/files/${fileId}/`),
  
  // Client Management (Enhanced with multi-tenant)
  getClients: async () => {
    try {
      const response = await api.get('/api/multi-tenant/clients/');
      const data = response.data;
      const list = Array.isArray(data) ? data : (data?.results ?? data?.data ?? []);
      return { ...response, data: list };
    } catch (error) {
      throw error;
    }
  },
  createClient: (clientData) => api.post('/api/multi-tenant/clients/', clientData),
  /** PATCH primary contact fields: name, email, phone, address, client_type */
  patchClientProfile: (clientId, data) => api.patch(`/api/multi-tenant/clients/${clientId}/`, data),
  getClient: async (clientId) => {
    try {
      const response = await api.get(`/api/multi-tenant/clients/${clientId}/`);
      return response;
    } catch (error) {
      throw error;
    }
  },
  checkDuplicate: (field, value) =>
    api.get('/api/clients/check-duplicate/', { params: { field, value } }),
  checkInvoiceNumber: (number) =>
    api.get('/api/invoices/check-number/', { params: { invoice_number: number } }),
  getClientHealthScore: (id) => api.get('/api/clients/' + id + '/health-score/'),
  sendSMS: (clientId, body) => api.post('/api/clients/' + clientId + '/sms/', { body }),
  getSMSHistory: (clientId) => api.get('/api/clients/' + clientId + '/sms/history/'),
  getHealthScores: (params) => api.get('/api/clients/health-scores/', { params }),
  getAtRiskClients: () => api.get('/api/clients/at-risk/'),
  updateClient: (clientId, clientData) => api.put(`/api/multi-tenant/clients/${clientId}/`, clientData),
  deleteClient: (clientId) => api.delete(`/api/multi-tenant/clients/${clientId}/`),

  /** Client notes: list/create use multi-tenant URL; delete matches registered `clients/<id>/notes/<note_id>/` route. */
  getClientNotes: (clientId) => api.get(`/api/multi-tenant/clients/${clientId}/notes/`),
  createClientNote: (clientId, payload) =>
    api.post(`/api/multi-tenant/clients/${clientId}/notes/`, payload),
  deleteClientNote: (clientId, noteId) =>
    api.delete(`/api/clients/${clientId}/notes/${noteId}/`),
  
  // Event Management (scheduling, client-linked events, reminders)
  getEvents: (params) => api.get('/api/multi-tenant/events/', { params: params || {} }),
  createEvent: (data) => api.post('/api/multi-tenant/events/', data),
  getEvent: (eventId) => api.get(`/api/multi-tenant/events/${eventId}/`),
  updateEvent: (eventId, data) => api.put(`/api/multi-tenant/events/${eventId}/`, data),
  deleteEvent: (eventId) => api.delete(`/api/multi-tenant/events/${eventId}/`),
  getClientEvents: (clientId, params) => api.get(`/api/multi-tenant/clients/${clientId}/events/`, { params: params || {} }),
  /** Client activity feed. Optional `params.types`: comma-separated e.g. `email`, `note`, `invoice`, `workflow`, `file_upload`, `communication`, `document_request`, `esignature`. */
  getClientTimeline: (clientId, params) => api.get(`/api/clients/${clientId}/timeline/`, { params: params || {} }),
  getChatMessages: (clientId) => api.get(`/api/clients/${clientId}/messages/`),
  sendChatMessage: (clientId, message) => api.post(`/api/clients/${clientId}/messages/`, { message }),
  markMessagesRead: (clientId) => api.post(`/api/clients/${clientId}/messages/read/`),
  getUnreadMessageCount: () => api.get('/api/messages/unread-count/'),
  getMyEvents: () => api.get('/api/multi-tenant/events/my/'),
  getEventDashboardStats: () => api.get('/api/multi-tenant/events/dashboard-stats/'),
  getEventComments: (eventId) => api.get(`/api/multi-tenant/events/${eventId}/comments/`),
  addEventComment: (eventId, content) => api.post(`/api/multi-tenant/events/${eventId}/comments/`, { content }),

  // Task Management
  getTasks: (params) => api.get('/api/tasks/', { params: params || {} }),
  createTask: (data) => api.post('/api/tasks/', data),
  getTask: (taskId) => api.get(`/api/tasks/${taskId}/`),
  updateTask: (taskId, taskData) => api.patch(`/api/tasks/${taskId}/`, taskData),
  deleteTask: (taskId) => api.delete(`/api/tasks/${taskId}/`),
  
  // Workflow Management (Enhanced with multi-tenant)
  getWorkflowAnalytics: () => api.get('/api/multi-tenant/workflow-analytics/'),
  getWorkflowTemplates: () => api.get('/api/multi-tenant/workflow-templates/'),
  createWorkflowTemplate: (templateData) => api.post('/api/multi-tenant/workflow-templates/', templateData),
  getWorkflowTemplate: (templateId) => api.get(`/api/multi-tenant/workflow-templates/${templateId}/`),
  updateWorkflowTemplate: (templateId, templateData) => api.put(`/api/multi-tenant/workflow-templates/${templateId}/`, templateData),
  deleteWorkflowTemplate: (templateId) => api.delete(`/api/multi-tenant/workflow-templates/${templateId}/`),
  
  // Workflow Tasks
  getWorkflowTaskTemplates: (templateId) => api.get(`/api/multi-tenant/workflow-templates/${templateId}/tasks/`),
  createWorkflowTaskTemplate: (templateId, taskData) => api.post(`/api/multi-tenant/workflow-templates/${templateId}/tasks/`, taskData),
  
  // Workflow Instances
  assignWorkflowToClient: (data) => api.post('/api/multi-tenant/assign-workflow/', data),
  getClientWorkflows: (clientId) => api.get(`/api/multi-tenant/clients/${clientId}/workflows/`),
  getAIWorkflowSuggestions: (clientId) => api.post('/api/ai/workflow-suggestions/', { client_id: clientId }),
  applyAIWorkflowSuggestion: (suggestionId, clientId) => api.post(`/api/ai/workflow-suggestions/${suggestionId}/apply/`, { client_id: clientId }),
  draftEmailResponse: (threadId) => api.post('/api/ai/draft-email-response/', { email_thread_id: threadId }),
  generateDocumentRequests: (clientId, workflowType) => api.post('/api/ai/generate-document-requests/', { client_id: clientId, workflow_type: workflowType }),
  completeWorkflowTask: (taskInstanceId) => api.post(`/api/multi-tenant/complete-task/${taskInstanceId}/`),
  
  // User Management (Enhanced with multi-tenant)
  getUsers: () => api.get('/api/multi-tenant/users/'),
  createUser: (userData) => api.post('/api/users/', userData),
  getUser: (userId) => api.get(`/api/multi-tenant/users/${userId}/`),
  updateUser: (userId, userData) => api.patch(`/api/multi-tenant/users/${userId}/`, userData),
  deleteUser: (userId) => api.delete(`/api/users/${userId}/`),
  inviteUser: (invitationData) => {
    let userId = localStorage.getItem('userId');
    
    // Try to get userId from user object if not directly available
    if (!userId) {
      const user = localStorage.getItem('user');
      if (user) {
        try {
          const userData = JSON.parse(user);
          userId = userData.id || userData.user_id;
        } catch (error) {
          console.error('Error parsing user data:', error);
        }
      }
    }
    
    if (!userId) {
      throw new Error('User ID not found. Please log in again.');
    }
    
    const url = `/api/add-user/?user=${userId}`;
    return api.post(url, invitationData);
  },
  
  // Platform Billing / Seats
  getSeatInfo: () => api.get('/api/platform/seats/'),
  addSeats: (quantity) => api.post('/api/platform/seats/add/', { seats: quantity }),
  getPlatformPlans: () => api.get('/api/platform/plans/'),
  checkFeatureAccess: (feature) => api.get('/api/platform/feature-check/', { params: { feature } }),

  getTips: () => api.get('/api/tips/'),
  dismissTip: (tipKey) => api.post(`/api/tips/${tipKey}/dismiss/`),

  // Billing Management (Enhanced with multi-tenant)
  getBillingData: () => api.get('/api/billing/'),
  getInvoices: () => api.get('/api/invoices/'),
  createInvoice: (invoiceData) => api.post('/api/invoices/', invoiceData),
  
  // Activity Log
  getActivityLog: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `/api/activity-log/?${queryString}` : '/api/activity-log/';
    return api.get(url);
  },
  getActivityLogStats: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `/api/activity-log/stats/?${queryString}` : '/api/activity-log/stats/';
    return api.get(url);
  },
  createActivityLog: (data) => api.post('/api/activity-log/', data),
  
  // Company Information (Multi-tenant)
  getCompanyInformation: () => api.get('/api/multi-tenant/company/information/'),
  updateCompanyInformation: (data) => api.put('/api/multi-tenant/company/information/', data),

  // Branding
  getBranding: () => api.get('/api/branding/'),
  updateBranding: (data) => api.patch('/api/branding/', data),
  getPublicBranding: (slug) => api.get('/api/public-branding/', { params: { company: slug } }),
  
  // Permission Sets (Multi-tenant)
  getPermissionSets: () => api.get('/api/multi-tenant/permission-sets/'),
  createPermissionSet: (data) => api.post('/api/multi-tenant/permission-sets/', data),
  updatePermissionSet: (id, data) => api.put(`/api/multi-tenant/permission-sets/${id}/`, data),
  deletePermissionSet: (id) => api.delete(`/api/multi-tenant/permission-sets/${id}/`),
  
  // Multi-tenant Tags Management
  getTags: () => api.get('/api/multi-tenant/tags/'),
  createTag: (data) => api.post('/api/multi-tenant/tags/', data),
  updateTag: (id, data) => api.put(`/api/multi-tenant/tags/${id}/`, data),
  deleteTag: (id) => api.delete(`/api/multi-tenant/tags/${id}/`),
  
  // Multi-tenant Teams Management
  getTeams: () => api.get('/api/multi-tenant/teams/'),
  createTeam: (data) => api.post('/api/multi-tenant/teams/', data),
  updateTeam: (id, data) => api.patch(`/api/multi-tenant/teams/${id}/`, data),
  deleteTeam: (id) => api.delete(`/api/multi-tenant/teams/${id}/`),

  // Multi-tenant Organizational Roles Management
  getOrganizationalRoles: () => api.get('/api/multi-tenant/organizational-roles/'),
  createOrganizationalRole: (data) => api.post('/api/multi-tenant/organizational-roles/', data),
  updateOrganizationalRole: (id, data) => api.put(`/api/multi-tenant/organizational-roles/${id}/`, data),
  deleteOrganizationalRole: (id) => api.delete(`/api/multi-tenant/organizational-roles/${id}/`),

  // Client Role Assignments (team members per role per client)
  getClientRoleAssignments: (clientId) => api.get(`/api/multi-tenant/clients/${clientId}/role-assignments/`),
  updateClientRoleAssignments: (clientId, data) => api.post(`/api/multi-tenant/clients/${clientId}/role-assignments/`, data),
  bulkUpdateClientRoleAssignments: (data) => api.post('/api/multi-tenant/clients/bulk-role-assignments/', data),
  
  // Multi-tenant User Profile Management
  getUserProfile: (userId = null) => {
    const url = userId ? `/api/multi-tenant/user-profile/?user=${userId}` : '/api/multi-tenant/user-profile/';
    return api.get(url);
  },
  updateUserProfile: (userId = null, data) => {
    const url = userId ? `/api/multi-tenant/user-profile/${userId}/` : '/api/multi-tenant/user-profile/';
    return api.patch(url, data);
  },
  
  // Multi-tenant Recent Clients Management
  getRecentClients: () => api.get('/api/multi-tenant/recent-clients/'),
  addRecentClient: (clientId) => api.post('/api/multi-tenant/recent-clients/add/', { client_id: clientId }),
  
  // Utility methods
  isMultiTenant: () => {
    // Check if we're in multi-tenant mode by looking for company context
    const user = localStorage.getItem('user');
    if (user) {
      try {
        const userData = JSON.parse(user);
        return userData.company_id !== undefined;
      } catch (error) {
        return false;
      }
    }
    return false;
  },
  
  getCompanyContext: () => {
    const user = localStorage.getItem('user');
    if (user) {
      try {
        const userData = JSON.parse(user);
        return userData.company_id;
      } catch (error) {
        return null;
      }
    }
    return null;
  },
  
  // Get all clients for search/assignment purposes
  getAllClients: async () => {
    const response = await api.get('/api/multi-tenant/clients/');
    const data = response.data;
    const list = Array.isArray(data) ? data : (data?.results ?? data?.data ?? []);
    return { ...response, data: list };
  },
  
  // Client Assignment Management
  getClientAssignments: (userId) => api.get(`/api/multi-tenant/users/${userId}/assigned-clients/`),
  assignClientsToUser: (userId, data) => api.post(`/api/multi-tenant/users/${userId}/assign-clients/`, data),
  
  // Dashboard Data
  getDashboardData: () => api.get('/api/dashboard-data/', { params: { mark_viewed: true } }),
  
  // User Management (Additional methods)
  getCurrentUser: () => api.get('/api/multi-tenant/users/current/'),
  updateCurrentUser: (data) => api.patch('/api/multi-tenant/users/current/', data),
  getCurrentUserPermissions: (userId) => api.get(`/api/multi-tenant/users/?user_id=${userId}`),
  
  // File Management
  getFiles: () => api.get('/api/multi-tenant/files/'),
  uploadFile: (formData) =>
    api.post(
      '/api/multi-tenant/files/upload/',
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    ),
  deleteFile: (fileId) => api.delete(`/api/multi-tenant/files/${fileId}/`),
  
  // Task Management (uses api/tasks/ - TaskManagementView)
  getTasks: (params) => api.get('/api/tasks/', { params: params || {} }),
  createTask: (data) => api.post('/api/tasks/', data),
  updateTask: (taskId, data) => api.patch(`/api/tasks/${taskId}/`, data),
  deleteTask: (taskId) => api.delete(`/api/tasks/${taskId}/`),
  
  // Workflow Management
  getWorkflows: (params) => api.get('/api/multi-tenant/workflows/', { params: params || {} }),
  getWorkflowPipeline: () => api.get('/api/multi-tenant/workflow-pipeline/'),
  moveWorkflowStage: (workflowId, pipelineStage) =>
    api.patch(`/api/multi-tenant/workflows/${workflowId}/move-stage/`, { pipeline_stage: pipelineStage }),
  getWorkflowAnalytics: () => api.get('/api/multi-tenant/workflow-analytics/'),
  createWorkflow: (data) => api.post('/api/multi-tenant/workflows/', data),
  updateWorkflow: (workflowId, data) => api.patch(`/api/multi-tenant/workflows/${workflowId}/`, data),
  deleteWorkflow: (workflowId) => api.delete(`/api/multi-tenant/workflows/${workflowId}/`),
  
  // Notification Management
  getNotifications: () => api.get('/api/multi-tenant/notifications/'),
  markNotificationRead: (notificationId) => api.patch(`/api/multi-tenant/notifications/${notificationId}/read/`),
  markAllNotificationsRead: () => api.post('/api/multi-tenant/notifications/mark-all-read/'),
  getNotificationStats: () => api.get('/api/multi-tenant/notifications/dashboard-stats/'),
  getInboxMessages: () => api.get('/api/multi-tenant/inbox/'),
  getInboxClients: () => api.get('/api/multi-tenant/inbox/clients/'),
  getInboxClientConversation: (clientId) => api.get(`/api/multi-tenant/inbox/clients/${clientId}/`),
  replyToInboxClient: (clientId, data) => api.post(`/api/multi-tenant/inbox/clients/${clientId}/`, data),
  getInboxMessage: (messageId) => api.get(`/api/multi-tenant/inbox/${messageId}/`),
  getInboxThread: (messageId) => api.get(`/api/multi-tenant/inbox/${messageId}/thread/`),
  markInboxMessageRead: (messageId) => api.patch(`/api/multi-tenant/inbox/${messageId}/`),
  replyToInboxMessage: (messageId, data) => api.post(`/api/multi-tenant/inbox/${messageId}/reply/`, data),
  sendBulkEmail: (data) => api.post('/api/multi-tenant/bulk-email/', data),
  sendBulkDocumentRequest: (data) => api.post('/api/bulk-document-requests/', data),

  // E-Signature
  requestWetSignature: (id) => api.post(`/api/esignatures/${id}/request-wet-signature/`),
  confirmWetSignature: (id) => api.post(`/api/esignatures/${id}/confirm-wet-signature/`),
  prepareESignatureFields: (id, fields) => api.post(`/api/esignatures/${id}/prepare/`, { fields }),
  sendESignatureDocument: (id) => api.post(`/api/esignatures/${id}/send/`),
  getESignatureFields: (id) => api.get(`/api/esignatures/${id}/fields/`),
  listESignatureDocuments: (params) => api.get('/api/esignature-documents/', { params: params || {} }),
  remindESignatureDocument: (id) => api.post(`/api/esignature-documents/${id}/reminder/`),

  // Email Suggestions (task suggestions from inbox)
  getEmailSuggestions: () => api.get('/api/email-suggestions/'),
  acceptEmailSuggestion: (id) => api.post(`/api/email-suggestions/${id}/accept/`),
  dismissEmailSuggestion: (id) => api.post(`/api/email-suggestions/${id}/dismiss/`),

  // Email Threads (assignee, status)
  getEmailThreads: (filter) => api.get('/api/email-threads/', { params: filter ? { filter } : {} }),
  getEmailThread: (id) => api.get(`/api/email-threads/${id}/`),
  assignEmailThread: (id, userId) => api.post(`/api/email-threads/${id}/assign/`, { assigned_to: userId }),
  updateEmailThreadStatus: (id, status) => api.patch(`/api/email-threads/${id}/status/`, { status }),

  // Client Activity Feed
  getClientActivityFeed: (limit = 15) => api.get('/api/multi-tenant/client-activity-feed/', { params: { limit } }),

  // Audit Log
  getAuditLog: () => api.get('/api/multi-tenant/audit-log/'),
  
  // Search
  searchClients: (query) => api.get(`/api/multi-tenant/search/clients/?q=${encodeURIComponent(query)}`),
  searchUsers: (query) => api.get(`/api/multi-tenant/search/users/?q=${encodeURIComponent(query)}`),
  searchFiles: (query) => api.get(`/api/multi-tenant/search/files/?q=${encodeURIComponent(query)}`),

  // Global Search
  searchGlobal: (q) => api.get('/api/search/', { params: { q } }),
  getRecentSearches: () => api.get('/api/search/recent/'),

  // Time Entries / Timer
  getTimeEntries: (params) => api.get('/api/time-entries/', { params }),
  createTimeEntry: (data) => api.post('/api/time-entries/', data),
  updateTimeEntry: (id, data) => api.patch(`/api/time-entries/${id}/`, data),
  deleteTimeEntry: (id) => api.delete(`/api/time-entries/${id}/`),
  getTimeEntrySummary: (params) => api.get('/api/time-entries/summary/', { params }),
  getRunningTimer: () => api.get('/api/time-entries/running-timer/'),
  startTimer: (data) => api.post('/api/time-entries/start-timer/', data),
  stopTimer: (data) => api.post('/api/time-entries/stop-timer/', data),
  generateInvoiceFromTime: (data) => api.post('/api/time-entries/generate-invoice/', data),

  // Recurring Invoices / Subscriptions
  getSubscriptions: (params) => api.get('/api/subscriptions/', { params }),
  createSubscription: (data) => api.post('/api/subscriptions/', data),
  updateSubscription: (id, data) => api.patch(`/api/subscriptions/${id}/`, data),
  cancelSubscription: (id) => api.delete(`/api/subscriptions/${id}/`),
  getSubscriptionHistory: (id) => api.get(`/api/subscriptions/${id}/history/`),

  // Multi-tenant Folder Management
  getClientFolders: (clientId, includeArchived = false) =>
    api.get(`/api/multi-tenant/clients/${clientId}/folders/`, { params: includeArchived ? { include_archived: 'true' } : {} }),
  createClientFolder: (clientId, data) => api.post(`/api/multi-tenant/clients/${clientId}/folders/`, data),
  getClientFiles: (clientId, includeArchived = false) =>
    api.get(`/api/multi-tenant/clients/${clientId}/files/`, { params: includeArchived ? { include_archived: 'true' } : {} }),
  getFolderContents: (clientId, folderId, includeArchived = false) =>
    api.get(`/api/multi-tenant/clients/${clientId}/folders/${folderId}/contents/`, { params: includeArchived ? { include_archived: 'true' } : {} }),
  uploadFileToFolder: (clientId, folderId, formData) => api.post(`/api/multi-tenant/clients/${clientId}/folders/${folderId}/files/`, formData),
  updateFolder: (clientId, folderId, data) => api.patch(`/api/multi-tenant/clients/${clientId}/folders/${folderId}/`, data),
  updateFolderFile: (clientId, folderId, fileId, data) => api.patch(`/api/multi-tenant/clients/${clientId}/folders/${folderId}/files/${fileId}/`, data),
  renameFile: (clientId, fileId, data) => api.patch(`/api/multi-tenant/clients/${clientId}/files/${fileId}/rename/`, data),
  deleteFile: (clientId, fileId) => api.delete(`/api/multi-tenant/clients/${clientId}/files/${fileId}/delete/`),
  getFileClassification: (id) => api.get(`/api/files/${id}/classification/`),
  moveFileToFolderById: (id, folderId) => api.post(`/api/files/${id}/move/`, { folder_id: folderId }),
  getFileMatchSuggestion: (id) => api.get(`/api/files/${id}/match-suggestion/`),

  // Multi-tenant Internal Folders
  getInternalFolders: () => api.get('/api/multi-tenant/internal-folders/'),
  getInternalFolderFiles: (folderId) => api.get(`/api/multi-tenant/internal-folders/${folderId}/files/`),
  
  // Multi-tenant Internal Files and Folders
  getInternalFiles: () => api.get('/api/multi-tenant/internal-files/'),
  getInternalFolders: () => api.get('/api/multi-tenant/internal-folders/'),
  getInternalFolderFiles: (folderId) => api.get(`/api/multi-tenant/internal-folders/${folderId}/files/`),
  getInternalFolderContents: (folderId) => api.get(`/api/multi-tenant/internal-folders/${folderId}/contents/`),
  uploadInternalFile: (folderId, formData) =>
    api.post(
      `/api/multi-tenant/internal-folders/${folderId}/files/`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    ),
  createInternalFolder: (data) => api.post('/api/multi-tenant/internal-folders/', data),
  
  // Multi-tenant Personal Files and Folders
  getPersonalFiles: () => api.get('/api/multi-tenant/personal-files/'),
  getPersonalFolders: () => api.get('/api/multi-tenant/personal-folders/'),
  getPersonalFolderFiles: (folderId) => api.get(`/api/multi-tenant/personal-folders/${folderId}/files/`),
  getPersonalFolderContents: (folderId) => api.get(`/api/multi-tenant/personal-folders/${folderId}/contents/`),
  uploadPersonalFile: (folderId, formData) =>
    api.post(
      `/api/multi-tenant/personal-folders/${folderId}/files/`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    ),

  createPersonalFolder: (data) => api.post('/api/multi-tenant/personal-folders/', data),
  
      // Other API endpoints
    addRecentlyViewedFile: (data) => api.post('/api/recently-viewed-files/', data),
    moveFile: (data) => api.post('/api/multi-tenant/move-file/', data),
    moveFileToFolder: (clientId, fileId, data) => api.post(`/api/multi-tenant/clients/${clientId}/files/${fileId}/move/`, data),
    
    // Stripe Connect endpoints
    getStripeConnectStatus: () => api.get('/api/multi-tenant/stripe/connect/'),
    createStripeConnectAccount: () => api.post('/api/multi-tenant/stripe/connect/'),
    getStripeOnboardingLink: () => api.get('/api/multi-tenant/stripe/onboarding/'),
    checkStripeAccountStatus: () => api.post('/api/multi-tenant/stripe/account-status/'),

  // Email integration
  getEmailIntegrationStatus: () => api.get('/api/email-integration-status/'),
  initiateGmailAuth: () => api.post('/api/gmail-auth/'),
  disconnectGmail: () => api.delete('/api/gmail-disconnect/'),

  // Google Calendar scheduling
  getCalendarStatus: () => api.get('/api/calendar-auth/status/'),
  initiateCalendarAuth: () => api.post('/api/calendar-auth/'),
  disconnectCalendar: () => api.post('/api/calendar-auth/disconnect/'),
  getMeetingTypes: () => api.get('/api/meeting-types/'),
  createBookingLink: (clientId, meetingTypeId) => api.post('/api/calendar/booking-link/', {
    client_id: clientId,
    meeting_type_id: meetingTypeId || null
  }),
  getCalendarSlots: (params) => api.get('/api/calendar/slots/', { params }),
  createMeeting: (data) => api.post('/api/calendar/meetings/', data),

  // QuickBooks
  getQuickBooksStatus: () => api.get('/api/quickbooks/status/'),
  getQuickBooksConnectUrl: () => api.get('/api/quickbooks/connect/'),
  disconnectQuickBooks: () => api.post('/api/quickbooks/disconnect/'),
  getQuickBooksItems: () => api.get('/api/quickbooks/items/'),
  syncClientToQuickBooks: (clientId) => api.post(`/api/quickbooks/sync-client/${clientId}/`),
  syncInvoiceToQuickBooks: (invoiceId) => api.post(`/api/quickbooks/sync-invoice/${invoiceId}/`),
  deleteInvoice: (invoiceId) => api.delete(`/api/multi-tenant/invoices/${invoiceId}/delete/`),
  syncAllToQuickBooks: () => api.post('/api/quickbooks/sync-all/'),
  // Client QB connection
  connectClientQB: (clientId) => api.get(`/api/quickbooks/client/${clientId}/connect/`),
  getClientQBStatus: (clientId) => api.get(`/api/quickbooks/client/${clientId}/status/`),
  disconnectClientQB: (clientId) => api.post(`/api/quickbooks/client/${clientId}/disconnect/`),
  pullClientFinancials: (clientId, data) => api.post(`/api/quickbooks/client/${clientId}/pull-financials/`, data),
  getClientQBReports: (clientId) => api.get(`/api/quickbooks/client/${clientId}/reports/`),
  // Firm QB reports
  getFirmQBReport: (type, start, end) => api.get(`/api/quickbooks/firm/report/?type=${type}&start=${start}&end=${end}`),
  getFirmQBExpenses: (start, end) => api.get(`/api/quickbooks/firm/expenses/?start=${start}&end=${end}`),
  toggleQBAutoSync: (enabled) => api.post('/api/quickbooks/auto-sync/', { enabled }),
  // QB Customer Sync
  syncQBCustomers: () => api.post('/api/quickbooks/sync-customers/'),
  getQBUnsyncedCustomers: () => api.get('/api/quickbooks/unsynced-customers/'),

  // Organizer Templates
  getOrganizerTemplates: () => api.get('/api/organizer-templates/'),
  createOrganizerFromTemplate: (templateId, data) => api.post(`/api/organizers/from-template/${templateId}/`, data),

  // Organizer Documents
  uploadOrganizerDocument: (organizerId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/api/organizers/${organizerId}/documents/`, fd);
  },
  getOrganizerDocuments: (organizerId) => api.get(`/api/organizers/${organizerId}/documents/`),
  deleteOrganizerDocument: (organizerId, docId) => api.delete(`/api/organizers/${organizerId}/documents/${docId}/`),

};

/**
 * Events calendar page — list/create/update/delete firm events.
 * Uses multi-tenant events API (PUT accepts partial payloads on the backend).
 */
export const getEvents = (params) => api.get('/api/multi-tenant/events/', { params: params || {} });
export const createEvent = (data) => api.post('/api/multi-tenant/events/', data);
export const updateEvent = (id, data) => api.put(`/api/multi-tenant/events/${id}/`, data);
export const deleteEvent = (id) => api.delete(`/api/multi-tenant/events/${id}/`);

/**
 * Client linked contacts (junction + portal permissions).
 * Backend: GET/POST `/api/clients/:id/linked-contacts/`, PATCH/DELETE `.../linked-contacts/:contactId/`,
 * POST invite `.../invite/`. Search: GET `/api/contacts/?search=`.
 */
export const getClientContacts = (clientId) => api.get(`/api/clients/${clientId}/linked-contacts/`);
export const addClientContact = (clientId, data) => api.post(`/api/clients/${clientId}/linked-contacts/`, data);
export const updateClientContact = (clientId, contactId, data) =>
  api.patch(`/api/clients/${clientId}/linked-contacts/${contactId}/`, data);
export const removeClientContact = (clientId, contactId) =>
  api.delete(`/api/clients/${clientId}/linked-contacts/${contactId}/`);
export const inviteContactPortal = (clientId, contactId) =>
  api.post(`/api/clients/${clientId}/linked-contacts/${contactId}/invite/`);
export const searchContacts = (query) => api.get('/api/contacts/', { params: { search: query } });
export const createContactRecord = (data) => api.post('/api/contacts/', data);

// Export both the axios instance and the enhanced API (API_BASE_URL for use in fetch/other non-axios calls)
export default api;
export { enhancedApi, API_BASE_URL }; 